"use client";
import { useState, useEffect } from "react";
import { FaPlus, FaRegFolder } from "react-icons/fa";
import { HiDotsVertical } from "react-icons/hi";
import { RxDragHandleDots2 } from "react-icons/rx";

import { IoMdCloseCircleOutline } from "react-icons/io";

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import folderService from "../../../services/folderServices";
import { useMeetingContext } from "../../../context/MeetingContext";
import { Link } from "react-router-dom";
import { TfiAngleLeft } from "react-icons/tfi";
import { IoMdClose } from "react-icons/io";
import FolderIcon from "../../../images/icons/folder.svg";

// Color palette matching the studio sidebar
const FOLDER_COLORS = [
  '#6a1fbf', '#00c853', '#ff5722', '#2196f3',
  '#9c27b0', '#ff9800', '#e91e63', '#00bcd4'
];

const FolderList = ({ onFolderSelect }) => {
  const {
    getFolders,
    loading,
    setLoading,
    error,
    setError,
    folders,
    setFolders,
    setCtaUpdateData,
    setUpdatePresetId,
  } = useMeetingContext();

  const [selectedFolder, setSelectedFolder] = useState(null);
  // const [folders, setFolders] = useState([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [menuOpenFor, setMenuOpenFor] = useState(null);

  // Form states
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderColor, setNewFolderColor] = useState("#6a1fbf");
  const [editFolderName, setEditFolderName] = useState("");
  const [editFolderColor, setEditFolderColor] = useState("#6a1fbf");
  const [hasFolders, setHasFolders] = useState(false);

  useEffect(() => {
    setUpdatePresetId("");
  }, [setUpdatePresetId]);

  // Load folders on component mount
  useEffect(() => {
    getFolders();
  }, [getFolders]);

  useEffect(() => {
    const handleClickOutside = () => {
      setMenuOpenFor(null);
    };

    if (menuOpenFor) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menuOpenFor]);

  useEffect(() => {
    setCtaUpdateData(null);
  }, [setCtaUpdateData]);

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError("Folder name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const folderData = {
        name: newFolderName.trim(),
        color: newFolderColor,
      };

      await folderService.createFolder(folderData);

      setShowCreateModal(false);
      setNewFolderName("");
      setNewFolderColor("#6a1fbf");
      getFolders();
    } catch (err) {
      setError(err.message || "Failed to create folder");
    } finally {
      setLoading(false);
    }
  };

  // Update folder
  const handleUpdateFolder = async () => {
    if (!editingFolder || !editFolderName.trim()) {
      setError("Folder name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updateData = {
        name: editFolderName.trim(),
        color: editFolderColor,
      };

      const response = await folderService.updateFolder(
        editingFolder._id,
        updateData
      );

      if (response) {
        getFolders();
        setShowEditModal(false);
        setEditingFolder(null);
        setEditFolderName("");
        setEditFolderColor("#6a1fbf");
      }
    } catch (err) {
      setError(err.message || "Failed to update folder");
    } finally {
      setLoading(false);
    }
  };

  // Delete folder
  const handleDeleteFolder = async () => {
    if (!selectedFolder) {
      setError("No folder selected for deletion");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await folderService.deleteFolder(selectedFolder);

      if (response) {
        getFolders();
        setSelectedFolder(null);
        setShowDeleteModal(false);
      }
    } catch (err) {
      setError(err.message || "Failed to delete folder");
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (folder) => {
    setEditingFolder(folder);
    setEditFolderName(folder.name);
    setEditFolderColor(folder.color || "#6a1fbf");
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (selectedFolder) => {
    if (!selectedFolder) {
      setError("Please select a folder to delete");
      return;
    }
    setShowDeleteModal(true);
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder._id);
    onFolderSelect?.(folder);
  };

  useEffect(() => {
    setHasFolders(folders.length > 0);
  }, [folders]);
  

  const DeleteFolderModal = () => {
    const folderToDelete = folders.find((f) => f._id === selectedFolder);

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-[#00000038] text-white rounded-lg p-4 md:p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-semibold">
              Delete Folder
            </h3>
            <button
              onClick={() => setShowDeleteModal(false)}
              className="p-1 hover:bg-gray-700 rounded"
            >
              <IoMdCloseCircleOutline className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          <p className="text-gray-300 mb-4 md:mb-6 text-sm md:text-base">
            Are you sure you want to delete &quot;{folderToDelete?.name}&quot;?
            This action cannot be undone.
          </p>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="w-full sm:flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteFolder}
              disabled={loading}
              className="w-full sm:flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
            >
              {loading ? "Deleting..." : "Delete Folder"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleFolderDragEnd = async (result) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;

    // Work with the currently sorted list so indices match what the user sees
    const sorted = [...folders].sort((a, b) => (a.sortorder || 0) - (b.sortorder || 0));
    const [moved] = sorted.splice(source.index, 1);
    sorted.splice(destination.index, 0, moved);

    // Optimistic update — assign new sortorder values immediately
    const updatedFolders = sorted.map((f, index) => ({ ...f, sortorder: index }));
    setFolders(updatedFolders);

    const reorderedData = sorted.map((f, index) => ({
      id: f._id,
      order: index
    }));

    try {
      await folderService.reorderFolders(reorderedData);
    } catch (err) {
      console.error('Failed to reorder folders:', err);
      getFolders(); // revert on error
    }
  };

  const displayFolders = folders.length > 0 ? [...folders].sort((a, b) => (a.sortorder || 0) - (b.sortorder || 0)) : [];

  return (
    <div className="h-screen ">
      <div className="w-full max-w-8xl mx-auto p-3 md:p-6 primary-card-background rounded-xl md:rounded-2xl h-[calc(100vh-100px)]">
        {/* Error Display */}
        {error && (
          <div className="mb-3 md:mb-4 p-3 md:p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm md:text-base">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-400 hover:text-red-300"
            >
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-8 gap-3 md:gap-0">
          <h1 className="text-xl md:text-2xl font-semibold flex gap-3 md:gap-5 items-center uppercase text-white">
            <span>ELEMENTS</span>
            <span>
              <TfiAngleLeft />
            </span>
          </h1>

          {hasFolders && (
            <div className="flex items-center text-white gap-2 md:gap-4">
              {/* New Folder Button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-[#6a1fbf] hover:bg-[#5a19a0] rounded-lg transition-colors text-sm md:text-base"
              >
                <span className="font-medium">Create New</span>
                <img src={FolderIcon} alt="Folder" className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && folders.length === 0 && (
          <div className="text-center py-6 md:py-8">
            <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-white mx-auto"></div>
            <p className="mt-2 text-gray-400 text-sm md:text-base">
              Loading folders...
            </p>
          </div>
        )}

        {/* Folder Grid */}
        {!loading && (
          <DragDropContext onDragEnd={handleFolderDragEnd}>
            <Droppable droppableId="dashboard-folders" direction="horizontal">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6"
                >
                  {displayFolders.map((folder, index) => (
                    <Draggable key={folder._id} draggableId={folder._id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`p-3 md:p-4 rounded-lg md:rounded-xl cursor-pointer transition-all duration-200 relative group hover:brightness-110 ${
                            snapshot.isDragging ? 'shadow-2xl opacity-90 scale-105' : ''
                          }`}
                          style={{
                            backgroundColor: folder.color || '#6a1fbf',
                            ...provided.draggableProps.style,
                          }}
                        >
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="absolute top-2 left-2 p-1 cursor-grab active:cursor-grabbing z-10"
                            onClick={(e) => e.preventDefault()}
                          >
                            <RxDragHandleDots2 className="w-4 h-4 text-white/70" />
                          </div>

                          <Link to={`/dashboard/folders/element/${folder._id}`}>
                            <div className="flex items-start gap-2 md:gap-3">
                              <div className="w-7 h-7 md:w-8 md:h-8 bg-white rounded-md md:rounded-lg flex items-center justify-center flex-shrink-0">
                                <FaRegFolder className="text-xs md:text-sm" />
                              </div>
                              <div className="flex-1 text-white min-w-0">
                                <h3 className="font-medium truncate text-sm md:text-base">
                                  {folder.name}
                                </h3>
                                <p className="text-xs md:text-sm mt-1">Created by me</p>
                              </div>
                            </div>

                            {/* Actions Menu */}
                            <div className="absolute z-10 top-2 right-2">
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setMenuOpenFor(
                                      menuOpenFor === folder._id ? null : folder._id
                                    );
                                  }}
                                  className="p-1 bg-gray-700/80 hover:bg-gray-600/80 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                  title="Actions"
                                >
                                  <HiDotsVertical className="w-3 h-3" />
                                </button>

                                {menuOpenFor === folder._id && (
                                  <div
                                    className="absolute z-20 top-8 right-2 bg-[#2A2835] border border-gray-600 text-white rounded-md shadow-lg w-28"
                                    style={{
                                      boxShadow:
                                        "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        openEditModal(folder);
                                        setMenuOpenFor(null);
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-700"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setSelectedFolder(folder._id);
                                        openDeleteModal(folder._id);
                                        setMenuOpenFor(null);
                                      }}
                                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-700"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Click handler */}
                            <div
                              className="absolute inset-0"
                              onClick={() => handleFolderClick(folder)}
                            />
                          </Link>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Empty State */}
        {!loading && displayFolders.length === 0 && !showCreateModal && (
          <div className="text-center py-12 md:py-20 h-full flex flex-col items-center justify-center px-4">
            <div className="flex justify-center items-center mb-3 md:mb-4">
              <img
                src={FolderIcon}
                alt="Folder"
                className="w-12 h-12 md:w-16 md:h-16"
              />
            </div>
            <h3 className="text-xl md:text-2xl font-medium mb-2 text-white">
              Create a folder to get started
            </h3>
            <p className="text-white mb-6 md:mb-8 mx-auto text-sm md:text-base max-w-md">
              Keep all your elements and files organized in one place, whether
              for a brand, event, or team. Add more anytime you like.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-green-500 hover:bg-green-600 rounded-lg transition-colors mx-auto text-white text-sm md:text-base"
            >
              <span className="font-medium">Create Folder</span>
              <FaPlus className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Modals */}
        {showCreateModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#615B72] relative rounded-lg p-4 md:p-6 w-full max-w-md">
              <div className="absolute top-2 right-2 md:top-3 md:right-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-1 rounded cursor-pointer"
                >
                  <IoMdClose
                    color="white"
                    size={20}
                    className="md:w-6 md:h-6"
                  />
                </button>
              </div>

              <div className="flex items-center justify-center mb-3 md:mb-4">
                <img
                  src={FolderIcon}
                  alt="Folder"
                  className="w-12 h-12 md:w-auto md:h-auto"
                />
              </div>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-base md:text-lg text-white font-bold mb-2">
                    Name your folder *
                  </label>
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-sm md:text-base"
                    placeholder="Enter folder name"
                  />
                </div>

                <div>
                  <label className="block text-base md:text-lg text-white font-bold mb-2">
                    Color
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {FOLDER_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewFolderColor(c)}
                        className={`w-8 h-8 rounded-lg transition-all hover:scale-110 ${
                          newFolderColor === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#615B72]' : ''
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 md:mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewFolderColor("#6a1fbf");
                  }}
                  className="w-full sm:flex-1 px-4 py-2 border border-red-150 text-red-150 font-bold rounded-lg transition-colors cursor-pointer text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={loading}
                  className="w-full sm:flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 rounded-lg transition-colors text-white font-bold cursor-pointer text-sm md:text-base"
                >
                  {loading ? "Creating..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showEditModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-[#00000070] p-4">
            <div className="bg-[#00000038] text-white rounded-lg p-4 md:p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-semibold">
                  Edit Folder
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  <IoMdCloseCircleOutline className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium mb-2">
                    Folder Name *
                  </label>
                  <input
                    type="text"
                    value={editFolderName}
                    onChange={(e) => setEditFolderName(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-sm md:text-base"
                    placeholder="Enter folder name"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium mb-2">
                    Color
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {FOLDER_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setEditFolderColor(c)}
                        className={`w-8 h-8 rounded-lg transition-all hover:scale-110 ${
                          editFolderColor === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#00000038]' : ''
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 md:mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditFolderColor("#6a1fbf");
                  }}
                  className="w-full sm:flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateFolder}
                  disabled={loading}
                  className="w-full sm:flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
                >
                  {loading ? "Updating..." : "Update Folder"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteModal && <DeleteFolderModal />}
      </div>
    </div>
  );
};

export default FolderList;
